import { Component, h, Prop, State, Event, EventEmitter, Listen, Watch, Element } from '@stencil/core';
import { sanitize } from '@/utils/utils';
import { ThemeConfig } from '@nylas/core';

const DefaultPattern = {
  email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
  phone_number: /^\+?\d{1,15}$/,
};

const DefaultPlaceholder = {
  email: 'name@example.com',
  phone_number: '1234567890',
};

/**
 * The `input-component` component is a UI component that allows users to input text, email, or phone number values.
 * This component is used in the scheduling form to input text, email and phone number type inputs.
 */

@Component({
  tag: 'input-component',
  styleUrl: 'input-component.scss',
  shadow: true,
})
export class InputComponent {
  /**
   * The host element
   */
  @Element() el!: HTMLElement;
  /**
   * The name of the input. This is used to identify the input when submitting a form.
   */
  @Prop() name: string = 'input';
  /**
   * The default value of the input. This is the value that is displayed when the input is rendered.
   */
  @Prop() defaultValue?: string;
  /**
   * The label of the input. This is displayed above the input.
   */
  @Prop() label: string = '';
  /**
   * The type of the input. This is used to determine the input's behavior.
   * Supported types are 'text', 'email', and 'phone_number'.
   */
  @Prop() type: 'text' | 'email' | 'phone_number' | 'number' = 'text';
  /**
   * The placeholder of the input. This is displayed when the input is empty.
   */
  @Prop() placeholder: string = DefaultPlaceholder[this.type];
  /**
   * Whether the input is required. If true, the input must have a value when submitting a form.
   * Default is false. If the input is required and the value is empty, an error message is displayed.
   */
  @Prop() required: boolean = false;
  /**
   * Whether the input is read-only. If true, the input cannot be edited.
   * Default is false.
   */
  @Prop() readOnly: boolean = false;
  /**
   * Whether the input should be focused when rendered.
   * Default is false. If true, the input is focused when rendered.
   * Use this to set the focus on the first input in a form.
   */
  @Prop() autoFocus: boolean = false;
  /**
   * The pattern to validate the input value. If the value does not match the pattern, an error message is displayed.
   * Default is null. If the pattern is not set, the pattern is determined by the input type for 'email' and 'phone_number'.
   */
  @Prop() pattern?: RegExp;
  /**
   * The maximum length of the input value. If the value is longer than the maximum length, an error message is displayed.
   * Default is 255.
   */
  @Prop() maxLength: number = 255;
  /**
   * The error message to display when the value does not match the pattern.
   * Default is 'Invalid <field> format.' where <field> is the input label.
   */
  @Prop() patternError: string = '';

  /**
   * This error message is displayed when the input value is empty and the input is required.
   */
  @Prop() requiredError: string = '';
  @Prop() maxValue?: number; // Optional max value for 'number'
  @Prop() minValue?: number; // Optional min value for 'number'

  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The input value state.
   */
  @State() value!: string;
  /**
   * The error message state.
   */
  @State() error: string = '';

  /**
   * This event is fired when the input value is changed.
   * The scheduling form listens for this event to validate the input value and submit the form.
   * If using outside of the scheduling form, listen for this event to validate the input value
   * and handle the input value change.
   */
  @Event() nylasFormInputChanged!: EventEmitter<{
    value: string;
    name: string;
    label: string;
    type: string;
    error: string;
  }>;

  @Event() nylasFormInputFocused!: EventEmitter<{
    value: string;
    name: string;
  }>;

  @Event() nylasFormInputBlurred!: EventEmitter<{
    value: string;
    name: string;
  }>;

  // Lifecycle methods
  @Watch('defaultValue')
  handleDefaultValueChange(newValue: string) {
    this.value = sanitize(newValue);
    if (this.value) {
      this.validatePattern(this.value);
    }
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.el.style.setProperty(`${key}`, value);
      }
    }
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.el.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    // Theme already applied in connectedCallback
  }

  componentDidLoad() {
    this.value = sanitize(this.defaultValue || '');
    if (this.type !== 'text' && !this.pattern) {
      this.pattern = DefaultPattern[this.type];
    }

    if (this.value) {
      this.validatePattern(this.value);
    }
  }

  // Event listeners
  /**
   * Listen for the bookingFormSubmitted event to validate the input value when the form is submitted.
   */
  @Listen('bookingFormSubmitted', { target: 'document' })
  handleBookingFormSubmitted(event: CustomEvent) {
    this.validatePattern(this.value);
    if (this.error) {
      event.preventDefault();
    }
  }

  @Listen('formSubmitted', { target: 'document' })
  async handleFormSubmitted(event: CustomEvent) {
    if (this.el?.getAttribute('data-page-styling')) {
      this.validatePattern(this.value);
      if (this.error) {
        event.preventDefault();
      }
    }
  }

  // Methods
  handleInput(e: Event) {
    this.error = '';
    const inputValue = (e.target as HTMLInputElement).value;

    // For 'number', ensure the value is valid
    if (this.type === 'number') {
      const numericValue = parseFloat(inputValue);
      if ((this.maxValue !== undefined && numericValue > this.maxValue) || (this.minValue !== undefined && numericValue < this.minValue)) {
        if (this.minValue === undefined) {
          this.error = `Value must be less than ${this.maxValue}`;
        } else if (this.maxValue === undefined) {
          this.error = `Value must be greater than ${this.minValue}`;
        } else {
          this.error = `Value must be between ${this.minValue} and ${this.maxValue}`;
        }
      } else if (isNaN(numericValue)) {
        this.error = this.patternError || 'Invalid format.';
      }
    }

    this.value = sanitize(inputValue);
    this.nylasFormInputChanged.emit({
      value: this.value,
      name: this.name,
      label: this.label,
      error: this.error,
      type: this.type,
    });
  }

  handleBlur() {
    this.nylasFormInputBlurred.emit({
      value: this.value,
      name: this.name,
    });
  }

  handleFocus() {
    this.nylasFormInputFocused.emit({
      value: this.value,
      name: this.name,
    });
  }

  validatePattern(value: string) {
    // Reset error
    this.error = '';

    if (this.required && !value) {
      this.error = this.requiredError || 'This field is required.';
      return;
    }

    if (this.type === 'number' && value) {
      const numericValue = parseFloat(value);
      if ((this.maxValue !== undefined && numericValue > this.maxValue) || (this.minValue !== undefined && numericValue < this.minValue)) {
        this.error = `Value must be between ${this.minValue} and ${this.maxValue}`;
        return;
      }
    }

    if (this.pattern && value && !this.pattern.test(value)) {
      this.error = this.patternError || 'Invalid format.';
    }
  }

  render() {
    return (
      <label part={this.error ? 'ic__label ic__label--error' : 'ic__label'} class={{ error: !!this.error }}>
        {this.label && (
          <p>
            <span class="label">{this.label}</span>
            {this.required && <span class="required">*</span>}
          </p>
        )}
        <div part="ic__input_wrapper" class="input_wrapper">
          <input
            type={this.type}
            name={this.name}
            part={this.error ? 'ic__input ic__input--error' : 'ic__input'}
            title={this.readOnly ? 'read-only field' : undefined}
            readOnly={this.readOnly}
            autoFocus={this.autoFocus}
            value={this.value}
            maxLength={this.maxLength}
            max={this.type === 'number' ? this.maxValue : undefined}
            min={this.type === 'number' ? this.minValue : undefined}
            placeholder={this.placeholder}
            class={{ error: !!this.error }}
            onInput={e => this.handleInput(e)}
            onFocus={() => this.handleFocus()}
            onBlur={() => this.handleBlur()}
          />
          <slot name="additional-input"></slot>
        </div>
        {this.error && (
          <span class="error help-text" part="ic__error_message">
            {this.error}
          </span>
        )}
      </label>
    );
  }
}
